import FreestarAdSlot from '@freestar/pubfig-adslot-react-component';
import cx from 'classnames';
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {v5} from 'uuid';
import {initializeAmazonBids, refreshAmazonBids} from '~/ads/amazon';
import {
  CATEGORY_KEY,
  GPT_LOCAL_STORAGE_KEY,
  GPT_UUID,
  LOCATION_KEY,
  PAGE_TYPE_KEY,
  REFRESH_KEY,
  SECONDS_TO_WAIT_AFTER_VIEWABILITY,
} from '~/ads/constants';
import {type AdConfig, AdConfigKey, getAdConfig} from '~/ads/getAdConfig';
import {PlaceholderAd} from '~/common/fabric/GoogleAds/PlaceholderAd';
import {RemoveAdsButton} from '~/common/fabric/GoogleAds/RemoveAdsButton';
import {selectClassifiedCategory} from '~/common/fabric/reducers/eviteEvent/selectors';
import {useEffectEvent} from '~/common/hooks/useEffectEvent';
import {isFabricWebview} from '~/common/utils/webview';
import {selectPhotoGalleryOpen} from '~/ViewInvitation/selectors/photos';
import {selectUser} from '~/ViewInvitation/selectors/user';
import {Link} from '~library/atoms/Link';
import {Picture} from '~library/atoms/Picture';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';

import './GoogleAdBanner.sass';

export const PUBLISHER_PREFIX = 'evite-com';
const LAZY_LOAD_CONFIG = {
  fetchMarginPercent: 500, // Fetch ads when they are within 500% of the viewport
  renderMarginPercent: 200, // Render ads when they are within 200% of the viewport
  mobileScaling: 2.0, // Scale margins for mobile devices
};
const refreshAdSlot = async (
  adSlot: googletag.Slot,
  refreshCountRef: RefObject<number>,
  adConfig: AdConfig
) => {
  await refreshAmazonBids(adConfig);
  refreshCountRef.current += 1;
  adSlot.setConfig({
    targeting: {[REFRESH_KEY]: refreshCountRef.current.toString()},
  });
  window.googletag?.pubads?.()?.refresh([adSlot]);
};

enum AdEvents {
  SLOT_RESPONSE_RECEIVED = 'slotResponseReceived',
  IMPRESSION_VIEWABLE = 'impressionViewable',
  SLOT_VISIBILITY_CHANGED = 'slotVisibilityChanged',
}

type Props = {
  index?: number;
  configKey?: AdConfigKey;
  showRemoveButton?: boolean;
  isCreateEditPreview?: boolean;
  isGuestPreview?: boolean;
  removeOnClick?: (() => void) | null;
  location?: string;
  pageType?: string;
};

export const GoogleAdBanner = ({
  index = 0,
  configKey = AdConfigKey.TOP_LEADERBOARD,
  showRemoveButton = false,
  isCreateEditPreview = false,
  isGuestPreview = false,
  removeOnClick = null,
  location = '',
  pageType = '',
}: Props) => {
  const isSidePanel = configKey === AdConfigKey.SIDE_PANEL;
  const isLeaderboard = [
    AdConfigKey.VIEW_INVITE_MID_DESKTOP,
    AdConfigKey.VIEW_INVITE_MID_MOBILE,
    AdConfigKey.VIEW_INVITE_BOTTOM_DESKTOP,
    AdConfigKey.VIEW_INVITE_BOTTOM_MOBILE,
    AdConfigKey.TOP_LEADERBOARD,
  ].includes(configKey);
  const isPhotoShareAd = [AdConfigKey.PHOTO_SHARE_GALLERY, AdConfigKey.PHOTO_SHARE].includes(
    configKey
  );
  const isLargishAndUp = useIsLargishAndUp();
  const useFakeAds = isPhotoShareAd ? false : !!(isGuestPreview || isCreateEditPreview);
  const shouldRefresh = evite.features.isEnabled('auto_refresh_ads', false);
  const shouldRenderPrebid = evite.features.isEnabled('render_freestar_prebid', false);
  const shouldShowRemoveButton =
    (showRemoveButton &&
      !isGuestPreview &&
      !((isFabricWebview() || window.is_webview) && !isGuestPreview)) ||
    false;

  const isMounted = useRef(true);
  const refresh = useRef(false);
  const inFrame = useRef(false);
  const readyToListen = useRef(false);
  const adRef: RefObject<HTMLDivElement | null> = useRef(null);
  const adSlotRef: RefObject<googletag.Slot | null> = useRef(null);
  const refreshCountRef: RefObject<number> = useRef(0);
  const refreshAdTimeoutRef: RefObject<ReturnType<typeof setTimeout> | null> = useRef(null);

  const adConfig = useMemo(
    () => getAdConfig({index, isLargishAndUp, configKey}),
    [index, isLargishAndUp, configKey]
  );

  const freestarPlacementName = `${PUBLISHER_PREFIX}_${adConfig.adPlacementName}`;

  const [adOverride, setAdOverride] = useState<ReactNode>(null);
  const [unHideRemoveButton, setUnHideRemoveButton] = useState(useFakeAds && !isGuestPreview);

  const classifiedCategory = useSelector(selectClassifiedCategory);
  const photoGalleryOpen = useSelector(selectPhotoGalleryOpen);
  const isBlockedByGallery = photoGalleryOpen && !adConfig.adPlacementName.includes('photo');
  const {
    userId: loggedInUserId,
    guestId,
    hasUserOptedOut,
    emailSha256,
    emailMd5,
  } = useSelector(selectUser, shallowEqual);

  const getHashedPPID = useCallback(() => {
    const GPTValue = localStorage.getItem(GPT_LOCAL_STORAGE_KEY);
    const anonId = guestId || crypto.randomUUID();
    if (!(loggedInUserId || GPTValue)) {
      localStorage.setItem(GPT_LOCAL_STORAGE_KEY, v5(anonId, GPT_UUID));
    }
    const uid = (loggedInUserId || GPTValue) ?? anonId;
    return v5(uid, GPT_UUID);
  }, [guestId, loggedInUserId]);

  const hashedPPID = !hasUserOptedOut ? getHashedPPID() : '';

  if (pageType) adSlotRef.current?.setConfig({targeting: {[PAGE_TYPE_KEY]: pageType}});
  window.googletag?.pubads?.()?.setPublisherProvidedId(hashedPPID);

  const slotResponseListener = useEffectEvent((event: googletag.events.SlotResponseReceived) => {
    const {slot} = event;
    if (!slot) return;
    if (showRemoveButton) setUnHideRemoveButton(true);
  });

  const impressionViewableListener = useEffectEvent(
    (event: googletag.events.ImpressionViewableEvent) => {
      if (event.slot.getAdUnitPath() !== adConfig.adSlotUrl) return;
      if (!readyToListen.current) return;

      const {slot} = event;
      inFrame.current = true;

      if (slot && shouldRefresh) {
        if (refreshAdTimeoutRef.current) {
          clearTimeout(refreshAdTimeoutRef.current);
        }
        refreshAdTimeoutRef.current = setTimeout(async () => {
          if (isMounted.current && inFrame.current) {
            await refreshAdSlot(slot, refreshCountRef, adConfig);
          } else {
            refresh.current = true;
          }
        }, SECONDS_TO_WAIT_AFTER_VIEWABILITY * 1000);
      }
    }
  );

  const slotVisibilityListener = useEffectEvent(
    async (event: googletag.events.SlotVisibilityChangedEvent) => {
      if (event.slot.getAdUnitPath() !== adConfig.adSlotUrl) return;

      const {slot, inViewPercentage} = event;
      if (shouldRefresh && !shouldRenderPrebid) {
        inFrame.current = inViewPercentage >= 50;
        if (inFrame.current && refresh.current) {
          refresh.current = false;
          await refreshAdSlot(slot, refreshCountRef, adConfig);
        }
      }
    }
  );

  const initializeAd = useEffectEvent(() => {
    const elem = document.getElementById(adConfig.adPlacementName);
    if (adRef.current && adRef.current.id === adConfig.adPlacementName && elem) {
      window.googletag?.cmd.push(() => {
        if (!shouldRenderPrebid) {
          window.apstag?.setDisplayBids();
          window.googletag?.display(adConfig.adPlacementName);
        }
        evite.viewability?.trackAd(adConfig.adPlacementName);
        if (window.invitationPhotoAd === null || shouldRenderPrebid) return;

        window.googletag
          ?.pubads?.()
          ?.refresh([window.invitationPhotoAd], {changeCorrelator: false});
      });
    }
  });

  const initializeGoogleAdSlots = useEffectEvent((adConfig: AdConfig) => {
    if (!isMounted.current || adSlotRef.current) {
      // Don't initialize if unmounted or slot already exists
      return;
    }

    window.googletag?.cmd?.push(() => {
      if (!shouldRenderPrebid) {
        const adSlot = window.googletag.defineSlot(
          adConfig.adSlotUrl,
          adConfig.dimensions as googletag.GeneralSize,
          adConfig.adPlacementName
        );

        if (adSlot) {
          adSlot.setConfig({
            targeting: {
              [REFRESH_KEY]: refreshCountRef.current.toString(),
              [LOCATION_KEY]: location,
              [CATEGORY_KEY]: classifiedCategory,
            },
          });
          adSlotRef.current = adSlot;

          adSlot.addService(window.googletag?.pubads?.());
          window.invitationPhotoAd = adSlot;
          window.googletag.setConfig({lazyLoad: LAZY_LOAD_CONFIG});
          window.apstag?.setDisplayBids();
          // Initial timeout, so that all ads on page are evaluated on page load
          if (refreshAdTimeoutRef.current) {
            clearTimeout(refreshAdTimeoutRef.current);
          }
          refreshAdTimeoutRef.current = setTimeout(async () => {
            if (isMounted.current && inFrame.current) {
              await refreshAdSlot(adSlot, refreshCountRef, adConfig);
            } else {
              refresh.current = true;
            }
            readyToListen.current = true;
          }, SECONDS_TO_WAIT_AFTER_VIEWABILITY * 1000);
        }
      }

      window.googletag
        ?.pubads?.()
        ?.addEventListener(AdEvents.SLOT_RESPONSE_RECEIVED, slotResponseListener);

      window.googletag
        ?.pubads?.()
        ?.addEventListener(AdEvents.IMPRESSION_VIEWABLE, impressionViewableListener);

      window.googletag
        ?.pubads?.()
        ?.addEventListener(AdEvents.SLOT_VISIBILITY_CHANGED, slotVisibilityListener);
      initializeAd();
    });
  });

  const initializeAdOverride = useEffectEvent(async () => {
    if (window.ad_override?.image) {
      setAdOverride(
        <Link
          className="fabric-free-banner"
          data-qa-id="google-ad-override-link"
          href={window.ad_override.link || ''}
          as="transparent"
          target="_blank"
        >
          <Picture data-qa-id="google-ad-override-image" srcSet={window.ad_override.image} />
        </Link>
      );
    } else if (!shouldRenderPrebid && window.invitationPhotoAd && !isPhotoShareAd) {
      window.googletag?.cmd?.push(() => {
        if (window.invitationPhotoAd === null) return;

        window.googletag?.pubads?.()?.refresh([window.invitationPhotoAd]);
      });
    } else await initializeAmazonBids({index, isLargishAndUp, configKey});
    initializeGoogleAdSlots(adConfig);
  });

  useEffect(() => {
    if (!adOverride && !shouldRenderPrebid) {
      window.googletag?.cmd?.push(() => {
        if (window.slideshowSideAd === null) return;
        window.googletag?.pubads?.()?.refresh([window.slideshowSideAd]);
      });
    }
  }, [adOverride, shouldRenderPrebid]);

  useEffect(() => {
    initializeAdOverride();
  }, [initializeAdOverride]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      if (refreshAdTimeoutRef.current) {
        clearTimeout(refreshAdTimeoutRef.current);
        refreshAdTimeoutRef.current = null;
      }
      if (adSlotRef.current) {
        if (!shouldRenderPrebid) {
          window.googletag.destroySlots([adSlotRef.current]);
        }
        refreshCountRef.current = 0;
        adSlotRef.current = null;
        window.invitationPhotoAd = null;

        window.googletag
          .pubads()
          .removeEventListener(AdEvents.SLOT_RESPONSE_RECEIVED, slotResponseListener);
        window.googletag
          .pubads()
          .removeEventListener(AdEvents.IMPRESSION_VIEWABLE, impressionViewableListener);
        window.googletag
          .pubads()
          .removeEventListener(AdEvents.SLOT_VISIBILITY_CHANGED, slotVisibilityListener);
      }
    };
  }, [
    slotResponseListener,
    impressionViewableListener,
    slotVisibilityListener,
    shouldRenderPrebid,
  ]);

  return (
    <div
      className={cx('google-free-banner', {
        'google-free-banner--leaderboard': isLeaderboard,
        'google-free-banner--leaderboard-preview': isCreateEditPreview,
      })}
    >
      <div className="google-free-banner__wrapper-fit">
        {shouldShowRemoveButton && unHideRemoveButton && (
          <RemoveAdsButton
            removeOnClick={removeOnClick}
            sidePanel={isSidePanel}
            useFakeAds={useFakeAds}
          />
        )}
        {useFakeAds || isBlockedByGallery ? (
          <PlaceholderAd spacerOnly={false} sidePanel={isSidePanel} hideRealAds />
        ) : (
          <div
            data-qa-id="google-ad-banner"
            id={adConfig.adPlacementName}
            ref={adRef}
            className="gpt-banner"
          >
            {shouldRenderPrebid ? (
              <FreestarAdSlot
                publisher={PUBLISHER_PREFIX}
                placementName={freestarPlacementName}
                channel={adConfig.adPlacementName}
                targeting={{
                  [REFRESH_KEY]: refreshCountRef.current.toString(),
                  [LOCATION_KEY]: location,
                  [CATEGORY_KEY]: classifiedCategory,
                  [PAGE_TYPE_KEY]: pageType,
                }}
                onNewAdSlotsHook={() => {
                  window.freestar?.queue.push(() => {
                    window.freestar?.identity.setIdentity({
                      hashes: {
                        sha256: emailSha256,
                        md5: emailMd5,
                      },
                    });
                  });
                }}
              />
            ) : (
              <PlaceholderAd spacerOnly sidePanel={isSidePanel} hideRealAds={false} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
